/*
 * Copyright 2026 Sony Corporation
 */

import { Box, Typography } from '@mui/material';

import { panTiltOf, withPanTilt } from '../../scene/pan-tilt';
import type { Transform } from '../../scene/types';
import { NumberField } from './NumberField';

export interface DevicePosePanelProps {
  transform: Transform | undefined;
  /** Absent for a reader who may not move the device. */
  onChange?: (transform: Transform) => void;
}

/**
 * Where the device stands and where it points, typed rather than dragged.
 *
 * Still one writer: these fields hand their value to the same
 * `onTransformChanged` a gizmo drag ends in, so the two cannot race each other
 * for the same field (devplan 4.2).
 */
export const DevicePosePanel = ({ transform, onChange }: DevicePosePanelProps) => {
  if (!transform) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not placed yet.
      </Typography>
    );
  }

  const { position } = transform;
  const angles = panTiltOf(transform);
  const move = (axis: 'x' | 'y' | 'z', value: number) =>
    onChange?.({ ...transform, position: { ...position, [axis]: value } });
  const turn = (change: Parameters<typeof withPanTilt>[1]) => onChange?.(withPanTilt(transform, change));

  return (
    <Box className="flex flex-col gap-2">
      <Typography variant="caption" color="text.secondary">
        Position
      </Typography>
      <Box className="grid grid-cols-3 gap-2">
        {(['x', 'y', 'z'] as const).map((axis) => (
          <NumberField
            key={axis}
            label={axis.toUpperCase()}
            unit="m"
            value={position[axis]}
            disabled={!onChange}
            onCommit={(value) => move(axis, value)}
          />
        ))}
      </Box>

      <Typography variant="caption" color="text.secondary" className="mt-1">
        Aim
      </Typography>
      <Box className="grid grid-cols-3 gap-2">
        <NumberField
          label="Pan"
          unit="°"
          digits={1}
          value={angles.pan}
          disabled={!onChange}
          onCommit={(pan) => turn({ pan })}
        />
        <NumberField
          label="Tilt"
          unit="°"
          digits={1}
          min={-90}
          max={90}
          value={angles.tilt}
          disabled={!onChange}
          onCommit={(tilt) => turn({ tilt })}
        />
        <NumberField
          label="Roll"
          unit="°"
          digits={1}
          value={angles.roll}
          disabled={!onChange}
          onCommit={(roll) => turn({ roll })}
        />
      </Box>

      <Typography variant="caption" color="text.secondary" className="mt-1">
        Type a value, or drag the gizmo in the viewport. Zoom is the focal length, under Lens.
      </Typography>
    </Box>
  );
};
